// ADR 0023 Sprint 7 Wave B — Tenant selector band.
// Phase 3 (tenant-view switcher): added variant='global' for AppShell-level
// rendering, plus "Exit tenant view" affordance and persistent view-mode banner.
//
// Displays the currently selected tenant's name and a dropdown/combobox
// to switch among tenants returned by GET /platform/tenants.
//
// Authorization:
//   - Renders NOTHING for non-platform users.
//   - Gated on Me.is_platform_admin (ADR-0023). There is no 'platform:admin'
//     permission string on the real backend — the gate is the boolean field.
//   - The useIsPlatformAdmin check is UI-only; the backend enforces access via JWT.
//
// Selection:
//   - Writes to usePlatformTenant().setSelectedTenant.
//   - Session state only (see context.tsx security notes).
//
// Security:
//   - Tenant names are not PII — safe to render.
//   - Does NOT render subdomain or custom_domain in the selector (could
//     be used for subdomain enumeration by screen-scrapers). Just names + codes.
//
// Default-tenant pre-selection (2026-09):
//   - The dropdown opens with the platform/demo tenant (is_platform === true,
//     falling back to code === 'platform') sorted to the top and visually
//     marked "Default", so applying it is a single click.
//   - This is a UI convenience ONLY: it never calls setSelectedTenant on its
//     own. Scope is applied exclusively via the existing handleSelect click
//     handler, same as picking any other tenant — see context.tsx and
//     tenant-view-store.ts for why auto-apply is deliberately unsupported
//     (every tenant-scoped action while a platform admin is "in" a tenant is
//     audit-logged as acting_via_platform_override; that must stay tied to
//     an explicit click).
//   - Falls back gracefully to no default when the list carries no
//     platform/demo tenant (e.g. a production tenant admin's view).
//
// A way in when nothing is selected (2026-09 follow-up):
//   - Previously, variant='global' rendered NOTHING when no tenant was
//     selected — a platform admin landing on e.g. /my-dashboard had no
//     visible switcher at all and had to already know to go to /platform.
//   - Now it renders a compact, quiet "Select tenant" control that opens the
//     same dropdown (with the same default pre-highlighted). It is
//     deliberately NOT amber — that treatment stays reserved for the active
//     "Viewing as tenant: X" override; an unselected state is normal, not a
//     warning.
//   - De-duplication: routes that mount their own inline band
//     (PlatformLayout, AdminScenariosLayout, AdminRulesLayout — the latter
//     two are also reused for /admin/branches and /admin/teams) already show
//     a "no tenant selected" prompt with a Switch control on that page, so
//     showing the new compact entry there too would be a second picker.
//     TenantSelectorBandPresenceProvider (mounted once, in AppShell, wrapping
//     both the global band and the routed <Outlet />) lets an inline
//     instance register its presence on mount so the global instance can
//     suppress its own entry control — this mirrors the showExit prop below,
//     just flowing from a descendant back up to the shared ancestor instead
//     of from a parent down to a child.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Building2, ChevronDown, RefreshCw, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useIsPlatformAdmin } from '@/lib/permissions'
import { listTenants } from '../api'
import { usePlatformTenant } from '../context'
import type { TenantSummary } from '../types'

// ---- Inline-band presence registry -------------------------------------------
// Split into two contexts on purpose: the register function must be a STABLE
// reference (so the inline instance's registration effect only fires once per
// mount, not on every presence flip), while the boolean the global instance
// reads is expected to change over time. Combining them into one context
// value would make the register effect re-run every time hasInline flips,
// which — since registering flips hasInline — would loop.
const InlineBandRegisterContext = createContext<(() => () => void) | null>(null)
const InlineBandPresenceContext = createContext(false)

/**
 * Mount once, high enough to wrap BOTH the AppShell-level global band and the
 * routed <Outlet /> subtree that may contain a route-level inline band
 * (PlatformLayout, AdminScenariosLayout, AdminRulesLayout). Lets the global
 * band know — without hard-coding the route list, which has already grown
 * past its original three layouts — whether an inline band is present on the
 * current page, so it can avoid rendering a duplicate "select tenant" entry
 * control.
 */
export function TenantSelectorBandPresenceProvider({ children }: { children: ReactNode }) {
  const countRef = useRef(0)
  const [hasInline, setHasInline] = useState(false)

  const registerInline = useCallback(() => {
    countRef.current += 1
    if (countRef.current === 1) setHasInline(true)
    return () => {
      countRef.current -= 1
      if (countRef.current === 0) setHasInline(false)
    }
  }, [])

  return (
    <InlineBandRegisterContext.Provider value={registerInline}>
      <InlineBandPresenceContext.Provider value={hasInline}>
        {children}
      </InlineBandPresenceContext.Provider>
    </InlineBandRegisterContext.Provider>
  )
}

interface TenantSelectorBandProps {
  /**
   * 'inline' (default) — original route-level display inside PlatformLayout
   * and AdminScenariosLayout: full selection UI always visible.
   *
   * 'global' — AppShell-level display. When a tenant is selected, shows a
   * persistent "Viewing tenant: <name> — Exit" banner plus a Switch button.
   * When no tenant is selected, shows a compact, quiet "Select tenant" entry
   * control that opens the same dropdown — UNLESS the current route already
   * mounts its own inline band (see TenantSelectorBandPresenceProvider),
   * in which case it renders nothing to avoid a duplicate picker.
   */
  variant?: 'inline' | 'global'
  /**
   * When false, the "Exit tenant view" button is suppressed. Use this in
   * PlatformLayout where the AppShell-level global band (variant='global')
   * already provides Exit+Switch when a tenant is selected, preventing
   * duplicate exit/switch controls on the same screen.
   *
   * Defaults to true (Exit shown for all variants unless explicitly suppressed).
   */
  showExit?: boolean
}

export function TenantSelectorBand({ variant = 'inline', showExit = true }: TenantSelectorBandProps) {
  const isPlatformAdmin = useIsPlatformAdmin()

  // Gate: render nothing for non-platform users.
  if (!isPlatformAdmin) return null

  return <TenantSelectorBandInner variant={variant} showExit={showExit} />
}

interface TenantSelectorBandInnerProps {
  variant: 'inline' | 'global'
  showExit: boolean
}

function TenantSelectorBandInner({ variant, showExit }: TenantSelectorBandInnerProps) {
  const { selectedTenant, setSelectedTenant } = usePlatformTenant()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  // Presence registration: an inline instance tells the shared ancestor
  // provider it exists (see TenantSelectorBandPresenceProvider above); a
  // global instance reads that to decide whether to render its own entry
  // control. registerInline is a stable reference (useCallback, no deps) so
  // this effect only fires on mount/unmount, not on every presence flip.
  const registerInline = useContext(InlineBandRegisterContext)
  const hasInlineBand = useContext(InlineBandPresenceContext)

  useEffect(() => {
    if (variant !== 'inline' || !registerInline) return
    return registerInline()
  }, [variant, registerInline])

  const tenantsQ = useQuery({
    queryKey: ['platform', 'tenants'],
    queryFn: () => listTenants(),
    staleTime: 30_000,
  })

  // Memoized so the derived useMemo hooks below don't see a new array
  // identity on every render (tenantsQ.data?.items ?? [] would otherwise
  // create a fresh [] each render, defeating dependency comparison).
  const tenantsData = tenantsQ.data?.items
  const tenants = useMemo(() => tenantsData ?? [], [tenantsData])

  // Default-tenant resolution: prefer the explicit is_platform flag; fall
  // back to code === 'platform' for older payloads that predate the flag.
  // Resolves to null (no default) when no such tenant is present — e.g. a
  // real tenant admin's view never contains the platform/demo tenant.
  const defaultTenant = useMemo(
    () =>
      tenants.find((t) => t.is_platform === true) ??
      tenants.find((t) => t.code === 'platform') ??
      null,
    [tenants],
  )

  // Only meaningful as a "suggestion" while nothing is currently applied —
  // once a tenant is selected, the default no longer needs a visual nudge.
  const suggestedTenantId = selectedTenant ? null : (defaultTenant?.public_id ?? null)

  // Sort the default tenant to the top so applying it is a single click,
  // without needing to scroll or search.
  const ordered = defaultTenant
    ? [defaultTenant, ...tenants.filter((t) => t.public_id !== defaultTenant.public_id)]
    : tenants

  const filtered = search.trim()
    ? ordered.filter(
        (t) =>
          t.name.toLowerCase().includes(search.toLowerCase()) ||
          t.code.toLowerCase().includes(search.toLowerCase()),
      )
    : ordered

  const handleSelect = (tenant: TenantSummary) => {
    setSelectedTenant(tenant)
    setOpen(false)
    setSearch('')
  }

  const handleExit = () => {
    setSelectedTenant(null)
    setOpen(false)
    setSearch('')
  }

  // Global variant with no tenant selected: if the current route already
  // mounts its own inline band (PlatformLayout, AdminScenariosLayout,
  // AdminRulesLayout — the latter two are also reused for /admin/branches
  // and /admin/teams), that band already offers a "no tenant selected"
  // prompt + Switch control on the page, so rendering the compact entry
  // control here too would be a duplicate picker. Render nothing in that
  // case; otherwise fall through to the compact entry control below.
  if (variant === 'global' && !selectedTenant && hasInlineBand) return null

  // True only for the AppShell-level band when nothing is selected (and no
  // inline band already covers it) — drives the compact "Select tenant"
  // control instead of the amber banner or the full inline Exit/Switch row.
  const isCompactEntry = variant === 'global' && !selectedTenant

  return (
    <div
      className={
        variant === 'global'
          ? selectedTenant
            ? // Global (persistent) banner: amber background to make it unmissable.
              // The operator MUST always know they are acting within a specific tenant,
              // not in platform mode. This is a compliance-critical affordance.
              'relative z-10 flex items-center gap-2 border-b border-amber-300 bg-amber-50 px-4 py-1.5 text-sm dark:border-amber-700 dark:bg-amber-950/40'
            : // Quiet entry affordance: an unselected state is normal, not a
              // warning, so it must not compete with the amber "Viewing as
              // tenant" treatment above. No border/background — just a
              // compact, low-emphasis control offering a way into tenant view.
              'relative z-10 inline-flex items-center gap-1.5 px-4 py-1.5 text-sm text-muted-fg'
          : // Inline (route-level): neutral muted background.
            'relative flex items-center gap-2 border-b border-border bg-muted/30 px-4 py-2 text-sm'
      }
      role="status"
      aria-live={variant === 'global' ? 'polite' : undefined}
      aria-label={
        variant === 'global' && selectedTenant
          ? `Platform admin tenant-view mode: viewing ${selectedTenant.name}`
          : undefined
      }
    >
      <Building2 size={14} className="shrink-0 text-muted-fg" aria-hidden />

      {selectedTenant ? (
        <span className="flex items-center gap-2">
          {variant === 'global' && (
            <span className="font-semibold text-amber-700 dark:text-amber-300">
              Viewing as tenant:
            </span>
          )}
          <span className="font-medium">{selectedTenant.name}</span>
          <span className="rounded bg-muted px-1.5 py-px font-mono text-[0.65rem] text-muted-fg">
            {selectedTenant.code}
          </span>
          <TenantStatusDot status={selectedTenant.status} />
        </span>
      ) : isCompactEntry ? (
        <Button
          size="sm"
          variant="ghost"
          className="gap-1.5 text-muted-fg hover:text-fg"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {tenantsQ.isFetching ? (
            <RefreshCw size={11} className="animate-spin" aria-hidden />
          ) : (
            <ChevronDown size={11} aria-hidden />
          )}
          Select tenant
        </Button>
      ) : (
        <span className="text-muted-fg">
          {tenantsQ.isLoading ? 'Loading tenants…' : 'No tenant selected — pick one to manage'}
        </span>
      )}

      {/* The compact entry control above already toggles the dropdown itself
          (there is nothing selected yet to Exit, and a second Switch button
          next to it would be a redundant, duplicate control), so this row is
          skipped entirely for isCompactEntry. */}
      {!isCompactEntry && (
        <div className="ml-auto flex items-center gap-1">
          {/* Exit tenant view — only shown when a tenant is selected and showExit is true.
              PlatformLayout passes showExit={false} to suppress this in the inline band
              because the AppShell-level global band already shows Exit+Switch when a
              tenant is selected. AdminScenariosLayout and AdminRulesLayout use the
              default showExit={true} so they retain the Exit button. */}
          {selectedTenant && showExit && (
            <Button
              size="sm"
              variant="ghost"
              className="gap-1 text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40"
              onClick={handleExit}
              aria-label="Exit tenant view and return to platform mode"
            >
              <LogOut size={11} aria-hidden />
              Exit tenant view
            </Button>
          )}

          <Button
            size="sm"
            variant="ghost"
            className="gap-1"
            onClick={() => setOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label="Switch tenant"
          >
            {tenantsQ.isFetching ? (
              <RefreshCw size={11} className="animate-spin" aria-hidden />
            ) : (
              <ChevronDown size={11} aria-hidden />
            )}
            Switch
          </Button>
        </div>
      )}

      {open && (
        <div
          role="listbox"
          aria-label="Select tenant"
          className="absolute left-0 top-full z-50 mt-0.5 w-80 rounded border border-border bg-card shadow-lg"
        >
          {/* Search */}
          <div className="border-b border-border p-2">
            <input
              type="text"
              role="searchbox"
              aria-label="Search tenants"
              className="w-full rounded border border-border bg-card px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="Search by name or code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false)
              }}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- combobox dropdown, expected UX
              autoFocus
            />
          </div>

          {/* Options */}
          <ul className="max-h-60 overflow-y-auto py-1">
            {tenantsQ.isLoading ? (
              <li className="px-3 py-2 text-xs text-muted-fg">Loading…</li>
            ) : filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-muted-fg">
                {search ? 'No match' : 'No tenants'}
              </li>
            ) : (
              filtered.map((t) => {
                const isApplied = selectedTenant?.public_id === t.public_id
                const isSuggested = suggestedTenantId === t.public_id
                return (
                  <li
                    key={t.public_id}
                    role="option"
                    aria-selected={isApplied || isSuggested}
                    className={`flex cursor-pointer items-center justify-between px-3 py-2 text-xs hover:bg-muted/40 aria-selected:bg-primary/10 aria-selected:text-primary ${
                      isSuggested && !isApplied ? 'ring-1 ring-inset ring-primary/30' : ''
                    }`}
                    onClick={() => handleSelect(t)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleSelect(t)
                    }}
                    tabIndex={0}
                  >
                    <span className="flex flex-col">
                      <span className="flex items-center gap-1.5 font-medium">
                        {t.name}
                        {isSuggested && !isApplied && (
                          <span
                            className="rounded bg-primary/10 px-1 py-px text-[0.6rem] font-semibold uppercase tracking-wide text-primary"
                            data-testid="tenant-option-default-badge"
                          >
                            Default
                          </span>
                        )}
                      </span>
                      <span className="font-mono text-[0.65rem] text-muted-fg">{t.code}</span>
                    </span>
                    <TenantStatusDot status={t.status} />
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

function TenantStatusDot({ status }: { status: TenantSummary['status'] }) {
  const cls: Record<TenantSummary['status'], string> = {
    active: 'bg-status-success',
    draft: 'bg-status-pending',
    suspended: 'bg-status-failed',
    archived: 'bg-muted',
  }
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${cls[status] ?? 'bg-muted'}`}
      title={status}
      aria-label={`Status: ${status}`}
    />
  )
}
